import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { AskMentor } from "@/components/AskMentor";
import { Radar, TrendingUp, TrendingDown, Loader2, Cpu, Target, ShieldAlert, Activity } from "lucide-react";

const STATE_STYLE = {
  "Active": { c: "#1C8CFF", bg: "rgba(28,140,255,0.15)" },
  "TP1 Hit": { c: "#22e07b", bg: "rgba(34,224,123,0.15)" },
  "TP2 Hit": { c: "#22e07b", bg: "rgba(34,224,123,0.18)" },
  "TP3 Hit": { c: "#00F593", bg: "rgba(0,245,147,0.2)" },
  "Stop Loss Hit": { c: "#ff5b5b", bg: "rgba(255,91,91,0.15)" },
  "Expired": { c: "#8fa0b8", bg: "rgba(143,160,184,0.12)" },
};

function TradingViewChart() {
  const ref = useRef(null);
  useEffect(() => {
    let cancelled = false;
    const build = () => {
      if (cancelled || !ref.current || !window.TradingView) return;
      ref.current.innerHTML = "";
      new window.TradingView.widget({
        autosize: true,
        symbol: "OANDA:XAUUSD",
        interval: "15",
        timezone: "Etc/UTC",
        theme: "dark",
        style: "1",
        locale: "en",
        toolbar_bg: "#0F0F0F",
        hide_side_toolbar: true,
        allow_symbol_change: false,
        container_id: "tv_gold_chart",
      });
    };
    if (window.TradingView) {
      build();
    } else {
      const existing = document.getElementById("tv-js-lib");
      if (existing) {
        existing.addEventListener("load", build);
      } else {
        const script = document.createElement("script");
        script.id = "tv-js-lib";
        script.src = "https://s3.tradingview.com/tv.js";
        script.async = true;
        script.onload = build;
        document.body.appendChild(script);
      }
    }
    return () => { cancelled = true; };
  }, []);
  return <div id="tv_gold_chart" ref={ref} className="w-full h-full" />;
}

export default function Signals() {
  const [session, setSession] = useState("");
  const [signals, setSignals] = useState([]);
  const [analyzing, setAnalyzing] = useState(true);

  useEffect(() => {
    let alive = true;
    const apply = (d) => { if (alive) { setSession(d?.session || ""); setSignals(d?.signals || []); } };
    // Runs the real scan, but keeps the "Mentor is analyzing…" animation on screen for a
    // minimum visible time so the client clearly sees the analysis happen before the
    // fresh signal is revealed. No signal is shown until the analysis completes.
    const scan = async () => {
      if (alive) setAnalyzing(true);
      const start = Date.now();
      let data = null;
      try { const r = await api.post("/signals/scan"); data = r.data; }
      catch { /* ignore */ }
      const wait = Math.max(0, 3600 - (Date.now() - start));
      setTimeout(() => {
        if (!alive) return;
        if (data) apply(data);
        setAnalyzing(false);
      }, wait);
    };
    const poll = async () => { try { const { data } = await api.get("/signals/active"); apply(data); } catch { /* ignore */ } };
    scan();
    const pi = setInterval(poll, 8000);
    const si = setInterval(scan, 45000);
    return () => { alive = false; clearInterval(pi); clearInterval(si); };
  }, []);

  return (
    <div className="px-5 pt-6 pb-10 space-y-5 fade-up">
      <div>
        <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
          <Radar className="h-6 w-6" style={{ color: "var(--sfx-accent)" }} /> Signal — Gold (XAUUSD)
        </h1>
        <p className="text-sm text-[#8fb4e6] mt-1">Live mentor reads on Gold — Smart Scalper strategy, synced with MT5 & Prop Firm.</p>
      </div>

      {/* Real TradingView chart (Gold) */}
      <div className="rounded-2xl border border-[#14233d] bg-[#0F0F0F] overflow-hidden" data-testid="tv-chart">
        <div className="h-[360px] w-full">
          <TradingViewChart />
        </div>
      </div>

      <AskMentor />

      <div className="flex items-center justify-between">
        <span className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6]">Active Signals · {session || "—"}</span>
        {analyzing && (
          <span className="flex items-center gap-1.5 text-[10px] text-[var(--sfx-accent)] font-mono-term" data-testid="signals-scanning">
            <Loader2 className="h-3 w-3 animate-spin" /> analyzing
          </span>
        )}
      </div>

      {analyzing && signals.length > 0 && (
        <div className="flex items-center gap-2 rounded-xl border border-[var(--sfx-accent)]/30 px-3 py-2"
          style={{ background: "var(--sfx-accent-soft)" }} data-testid="signals-analyzing-banner">
          <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: "var(--sfx-accent)" }} />
          <span className="text-xs text-white">Mentor is analyzing the market for a new setup — please wait…</span>
        </div>
      )}

      {analyzing && signals.length === 0 ? (
        <AnalyzingCard />
      ) : signals.length === 0 ? (
        <div className="rounded-2xl border border-[#14233d] bg-[#0A1120] p-10 text-center space-y-3" data-testid="signals-empty">
          <div className="mx-auto h-12 w-12 rounded-2xl flex items-center justify-center" style={{ background: "var(--sfx-accent-soft)" }}>
            <Cpu className="h-6 w-6" style={{ color: "var(--sfx-accent)" }} />
          </div>
          <p className="text-sm font-semibold text-white">No confirmed signal right now</p>
          <p className="text-xs text-[#8fb4e6]">The mentor only posts a signal when the market gives a high-probability setup. Sit tight — a new read will appear automatically.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="signals-list">
          {signals.map((s) => <SignalCard key={s.id} s={s} />)}
        </div>
      )}
    </div>
  );
}

const ANALYZE_STEPS = [
  "Reading live price action…",
  "Measuring momentum (RSI / MACD)…",
  "Confirming the trend direction…",
  "Checking key support & resistance…",
  "Validating with Smart Scalper strategy…",
  "Calculating Entry, Stop Loss & Take Profit…",
];

function AnalyzingCard() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep((s) => (s + 1) % ANALYZE_STEPS.length), 900);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="rounded-2xl border border-[var(--sfx-accent)]/30 bg-[#0A1120] p-8 text-center overflow-hidden relative"
      data-testid="signals-analyzing">
      {/* Radar animation */}
      <div className="relative mx-auto h-28 w-28 mb-5">
        <span className="absolute inset-0 rounded-full border-2 border-[var(--sfx-accent)]/20" />
        <span className="absolute inset-0 rounded-full border-2 border-[var(--sfx-accent)]/25 animate-ping" style={{ animationDuration: "2s" }} />
        <span className="absolute inset-2 rounded-full border border-[var(--sfx-accent)]/20 animate-ping" style={{ animationDuration: "2.6s" }} />
        <span className="absolute inset-0 rounded-full animate-spin" style={{ animationDuration: "1.8s", background: "conic-gradient(from 0deg, transparent 0deg, var(--sfx-accent) 60deg, transparent 90deg)", WebkitMask: "radial-gradient(circle, transparent 52%, #000 54%)", mask: "radial-gradient(circle, transparent 52%, #000 54%)" }} />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-11 w-11 rounded-2xl flex items-center justify-center" style={{ background: "var(--sfx-accent-soft)" }}>
            <Activity className="h-5 w-5 animate-pulse" style={{ color: "var(--sfx-accent)" }} />
          </div>
        </div>
      </div>

      <p className="font-heading text-base font-bold text-white">Mentor is analyzing the market</p>
      <p className="text-xs text-[#8fb4e6] mt-1">Scanning Gold (XAUUSD) — please wait…</p>

      <div className="mt-5 h-5">
        <p key={step} className="text-xs font-mono-term text-[var(--sfx-accent)] fade-up" data-testid="analyzing-step">
          {ANALYZE_STEPS[step]}
        </p>
      </div>

      {/* Progress bar */}
      <div className="mt-4 h-1 w-full max-w-xs mx-auto rounded-full bg-[#14233d] overflow-hidden">
        <div className="h-full rounded-full analyzing-bar" style={{ background: "var(--sfx-accent)" }} />
      </div>
    </div>
  );
}

function SignalCard({ s }) {
  const buy = s.direction === "BUY";
  const st = STATE_STYLE[s.signal_state] || STATE_STYLE.Active;
  return (
    <div data-testid={`signal-${s.asset}`} className="rounded-2xl border border-[#14233d] bg-[#0A1120] overflow-hidden fade-up">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#14233d]">
        <div className="flex items-center gap-2">
          <span className="font-heading font-bold text-lg">{s.asset}</span>
          <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold font-mono-term flex items-center gap-1 ${buy ? "bg-[#22e07b]/15 text-[#22e07b]" : "bg-[#ff5b5b]/15 text-[#ff5b5b]"}`}>
            {buy ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{s.direction}
          </span>
          <span className="px-1.5 py-0.5 rounded-md text-[9px] font-mono-term uppercase tracking-wide bg-[var(--sfx-accent-soft)] text-[var(--sfx-accent)] flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-[#00F593] animate-pulse" /> New
          </span>
        </div>
        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold font-mono-term" style={{ color: st.c, backgroundColor: st.bg }} data-testid="signal-status">{s.signal_state || "Active"}</span>
      </div>

      <div className="p-4 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <Cell label="Entry Price" value={s.entry} />
          <Cell label="Stop Loss" value={`${s.sl}  (${s.sl_pips}p)`} color="#ff5b5b" icon={<ShieldAlert className="h-3 w-3" />} />
        </div>
        <div className="space-y-1.5">
          <TpRow label="TP1" value={s.tp1} pips={s.tp1_pips} hit={["TP1 Hit", "TP2 Hit", "TP3 Hit"].includes(s.signal_state)} />
          <TpRow label="TP2" value={s.tp2} pips={s.tp2_pips} hit={["TP2 Hit", "TP3 Hit"].includes(s.signal_state)} />
          <TpRow label="TP3" value={s.tp3} pips={s.tp3_pips} hit={s.signal_state === "TP3 Hit"} />
        </div>
        <div className="flex items-center justify-between pt-1 border-t border-[#14233d]">
          <span className="text-xs text-[#8fb4e6]">Risk / Reward <b className="text-white">{s.rr || "1:2"}</b></span>
          <span className="font-heading text-lg font-bold" style={{ color: buy ? "#22e07b" : "#ff5b5b" }}>{s.confidence}%</span>
        </div>
      </div>
    </div>
  );
}

function Cell({ label, value, color, icon }) {
  return (
    <div className="rounded-xl border border-[#14233d] bg-[#060c18] p-3">
      <div className="font-mono-term text-[9px] uppercase tracking-widest text-[#5f7ba6] mb-1 flex items-center gap-1">{icon}{label}</div>
      <div className="font-heading text-base font-bold" style={color ? { color } : {}}>{value}</div>
    </div>
  );
}

function TpRow({ label, value, pips, hit }) {
  return (
    <div className={`flex items-center justify-between rounded-xl border px-3 py-2 ${hit ? "border-[#22e07b]/40 bg-[#22e07b]/10" : "border-[#14233d] bg-[#060c18]"}`}>
      <span className="flex items-center gap-1.5 text-xs"><Target className={`h-3.5 w-3.5 ${hit ? "text-[#22e07b]" : "text-[#5f7ba6]"}`} /><b className={hit ? "text-[#22e07b]" : "text-white"}>{label}</b>{hit && <span className="text-[9px] font-mono-term text-[#22e07b]">HIT</span>}</span>
      <span className="font-mono-term text-xs">{value} <span className="text-[#5f7ba6]">(+{pips} pips)</span></span>
    </div>
  );
}
